import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, analytics } from "@/lib/utils";
import { getTrackingFields } from "@/lib/leadTracking";
import { stageFreePassUserDataForAds } from "@/lib/googleAdsFreePassEnhanced";
import { getLocationDisplayName, LOCATION_FORM_ORDER } from "@/lib/locationUrls";
import { SEO } from "@/components/SEO";
import { TurnstileWidget } from "@/components/TurnstileWidget";

const SOURCE_VALUE = "Website Free Pass";

export default function FreePassPage() {
  const navigate = useNavigate();
  const formStartedAtRef = useRef(Date.now());
  const [locations, setLocations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    locationId: "",
    sex: "",
    consentEmail: false,
    consentSms: false,
    company: "",
  });

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API_URL}/locations`);
        const raw = response?.data;
        const list = Array.isArray(raw) ? raw : raw?.locations ?? [];
        setLocations(list);
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    fetchLocations();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.company?.trim()) return; // honeypot
    if (!turnstileToken) {
      toast.error("Please complete the verification.");
      return;
    }
    if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.email?.trim() || !formData.phone?.trim() || !formData.dateOfBirth?.trim() || !formData.locationId || !formData.sex) {
      toast.error("Please fill in all required fields");
      return;
    }
    const dobDate = new Date(formData.dateOfBirth);
    const minAgeDate = new Date();
    minAgeDate.setFullYear(minAgeDate.getFullYear() - 10);
    if (dobDate > minAgeDate) {
      toast.error("You must be at least 10 years old to request a free pass");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/free-pass`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone?.trim() || undefined,
          dateOfBirth: formData.dateOfBirth?.trim() || undefined,
          locationId: formData.locationId,
          sex: formData.sex,
          consentEmail: formData.consentEmail,
          consentSms: formData.consentSms,
          source: SOURCE_VALUE,
          company: formData.company,
          cf_turnstile_response: turnstileToken,
          tracking: getTrackingFields(),
          form_started_at: formStartedAtRef.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        analytics.trackFreePassSubmit(formData.locationId);
        if (!data.suppress_ads) {
          stageFreePassUserDataForAds({
            email: formData.email.trim(),
            phone: formData.phone?.trim() || "",
          });
        }
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          dateOfBirth: "",
          locationId: "",
          sex: "",
          consentEmail: false,
          consentSms: false,
          company: "",
        });
        toast.success("Request submitted! We'll be in touch soon.");
        navigate("/thank-you-3-day-guest-pass", { replace: true });
      } else {
        toast.error(data.error || "Something went wrong. Please try again.");
        if (res?.status === 400) setTurnstileToken("");
      }
    } catch (err) {
      console.error("Failed to submit free pass request:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="free-pass-page">
      <SEO
        title="1 Day Free Pass"
        description="Request a free 1 day pass to try Giant Fitness. Choose your club location and we'll get you set up."
        url="/free-pass"
      />
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-4">
              1 DAY FREE PASS
            </h1>
            <p className="text-gray-600 text-lg">
              Not sure yet? Try us free for a day. Fill out the form below and we'll get you set up.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="space-y-6 bg-white border-2 border-gray-200 rounded-xl p-6 md:p-8 shadow-lg"
            data-testid="free-pass-form"
          >
            <input type="hidden" name="source" value={SOURCE_VALUE} />
            <div
              className="absolute -left-[9999px] w-1 h-1 overflow-hidden"
              aria-hidden="true"
            >
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="form-group">
                <Label htmlFor="firstName" className="form-label">First Name *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                  placeholder="First name"
                  data-testid="free-pass-first-name"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="lastName" className="form-label">Last Name *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                  placeholder="Last name"
                  data-testid="free-pass-last-name"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="email" className="form-label">Email address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                placeholder="your@email.com"
                data-testid="free-pass-email"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="phone" className="form-label">Phone number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                placeholder="(123) 456-7890"
                data-testid="free-pass-phone"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="dateOfBirth" className="form-label">Date of Birth *</Label>
              <Input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 10); return d.toISOString().split("T")[0]; })()}
                className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900"
                data-testid="free-pass-date-of-birth"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="location" className="form-label">Club Location *</Label>
              <Select
                value={formData.locationId || undefined}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, locationId: value }))}
                required
              >
                <SelectTrigger className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900" data-testid="free-pass-location">
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {[...locations]
                    .filter((loc) => loc?.id != null)
                    .sort((a, b) => LOCATION_FORM_ORDER.indexOf(String(a.id)) - LOCATION_FORM_ORDER.indexOf(String(b.id)))
                    .map((loc) => {
                      const id = String(loc.id);
                      const label = getLocationDisplayName(id) ?? loc.name ?? id;
                      return (
                        <SelectItem key={id} value={id} className="text-gray-900 hover:bg-gray-100">
                          {label}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="sex" className="form-label">Sex *</Label>
              <Select
                value={formData.sex || undefined}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, sex: value }))}
                required
              >
                <SelectTrigger className="h-12 bg-white border-gray-300 focus:border-brand-orange text-gray-900" data-testid="free-pass-sex">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="Male" className="text-gray-900 hover:bg-gray-100">Male</SelectItem>
                  <SelectItem value="Female" className="text-gray-900 hover:bg-gray-100">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="consentEmail"
                  checked={formData.consentEmail}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, consentEmail: !!checked }))
                  }
                />
                <Label htmlFor="consentEmail" className="text-sm font-normal cursor-pointer">
                  I agree to receive email updates from Giant Fitness
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="consentSms"
                  checked={formData.consentSms}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, consentSms: !!checked }))
                  }
                />
                <Label htmlFor="consentSms" className="text-sm font-normal cursor-pointer">
                  I agree to receive SMS updates from Giant Fitness
                </Label>
              </div>
            </div>

            <p className="text-sm text-gray-600">
              Free Day Pass available for your first visit only. Must be over 18 years old. Photo ID is required to redeem your free day pass.
            </p>

            <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold uppercase tracking-wider"
              data-testid="free-pass-submit"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                  Submitting...
                </span>
              ) : (
                "Request Free Pass"
              )}
            </Button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
